import { CampaignViewModel, CompanyViewModel, ContactViewModel, IEntity } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { DemoContent } from '../../../../assetsBrowser/containers/StaticDrawerDemo';
import { ILocationState } from '../../../../models';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { accountHasComplianceRequired } from '../../../../models/UiUtils';
import {
	CampaignsApprovalViewModel,
	SocialMediaPostViewModel,
	UserSessionContext,
} from '../../../../viewmodels/AppViewModels';
import { ROUTES } from '../../../constants/routes';
import { BulkHwcBirthdayComposerPage } from '../../../containers/BulkHwcPages/BulkHwcBirthdayComposerPage';
import { BulkHwcFromContactFilterPage } from '../../../containers/BulkHwcPages/BulkHwcFromContactFilterPage';
import { BulkHwcFromContactSelectionPage } from '../../../containers/BulkHwcPages/BulkHwcFromContactSelectionPage';
import { BlogCampaignRoutes } from '../../../containers/blogs';
import { BulkEmailCancelled } from '../../../containers/bulkEmailApproval/BulkEmailCancelled';
import { Company } from '../../../containers/Company';
import { Contact } from '../../../containers/Contact';
import { Opportunity } from '../../../containers/dataBoards/Opportunity';
import { EditQueuedCampaign } from '../../../containers/EditQueuedCampaign';
import { MultiCampaignSchedulerRoutes } from '../../../containers/MultiCampaignScheduler';
import { MultiEmailScheduler } from '../../../containers/MultiCampaignScheduler/components/emails/MultiEmailScheduler';
import { SequenceTransitionScreen } from '../../../containers/MultiCampaignScheduler/components/SequenceTransitionScreen';
import { PostcardComposer } from '../../../containers/PostcardComposer';
import { AIScheduleSuccess } from '../../../containers/shared/AIScheduleSuccess';
import { EditSocialMediaPost } from '../../../containers/socialMedia/EditSocialMediaPost';
import { SocialMediaPostScheduleComplete } from '../../../containers/socialMedia/SocialMediaPostScheduleComplete';
import { ApprovalPreviewSocialMediaPost } from '../../../containers/socialMediaPostApproval/ApprovalPreviewSocialMediaPost';
import { TextingCampaignRoutes } from '../../../containers/TextingCampaign';
import { BrowseAndSendCampaigns } from '../../../containers/templates/BrowseAndSendCampaigns';
import { CampaignFollowupPage } from '../../../containers/templates/CampaignFollowupPage';
import { AICampaignReviewPage } from '../../../containers/templates/ReviewAICampaign/AICampaignReviewPage';
import { CreateAICampaign } from '../../../containers/templates/CreateAICampaign';
import { CampaignFromTemplatePage } from '../../../containers/templates/CampaignFromTemplatePage';
import { ReviewEmailCampaign } from '../../../containers/templates/ReviewTemplates/ReviewEmailCampaigns/ReviewEmailCampaign';
import { ReviewSocialPost } from '../../../containers/templates/ReviewTemplates/ReviewSocialPosts/ReviewSocialPost';
import { bs } from '../../../styles/styles';
import { AdvancedDonationReporting } from '../../AdvancedDonationReporting';
import { AdvancedReporting } from '../../AdvancedReporting';
import { AutomationsOnHold } from '../../automation/AutomationsOnHold';
import { IntegrationOwnerships } from '../../integrations/IntegrationOwnerships';
import { IRouterLocation, PrivateRoute } from '../../PrivateRoute';
import {
	IMeetingConfigModalLocationState,
	MeetingConfigModal,
} from '../../settings/PersonalSettings/MeetingConfigModal';
import { FullscreenModal } from '../FullscreenModal';
import { styleSheet } from './styles';

const _WebFullscreenModal = () => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();

	const onRenderEditQueuedCampaign = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<CampaignViewModel | CampaignsApprovalViewModel, any> =
			fullscreenModal?.history?.location?.state ?? {};
		return (
			<EditQueuedCampaign
				{...props}
				approval={locationState.viewModel instanceof CampaignsApprovalViewModel ? locationState.viewModel : undefined}
				campaign={locationState.viewModel instanceof CampaignViewModel ? locationState.viewModel : undefined}
			/>
		);
	};

	const onRenderAdvancedReportingDashboard = (props: RouteComponentProps<any>) => {
		return <AdvancedReporting {...props} />;
	};

	const onRenderAutomationsOnHold = () => {
		return <AutomationsOnHold />;
	};

	const onRenderBrowseAndSend = (props: RouteComponentProps<any>) => {
		return <BrowseAndSendCampaigns {...props} />;
	};

	const onRenderIntegrationOwnership = (props: RouteComponentProps<any>) => {
		return <IntegrationOwnerships {...props} />;
	};

	const onRenderBulkEmailCancelled = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<CampaignsApprovalViewModel, any> = fullscreenModal.history.location
			? fullscreenModal.history.location.state || {}
			: {};
		return <BulkEmailCancelled {...props} campaign={locationState?.viewModel} />;
	};

	const onRenderContact = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<ContactViewModel, boolean> = fullscreenModal.history.location
			? fullscreenModal.history.location.state || {}
			: {};
		return (
			<div className={css(styleSheet.defaultChildStyle)}>
				<Contact
					{...props}
					contact={locationState.viewModel}
					contactId={props.match.params.id}
					hideDeleteButton={locationState.model}
				/>
			</div>
		);
	};

	const onRenderCompany = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<CompanyViewModel, IEntity> = fullscreenModal.history.location
			? fullscreenModal.history.location.state || {}
			: {};
		return (
			<Company
				{...props}
				company={locationState.viewModel}
				companyId={props.match.params.id}
				styles={[styleSheet.defaultChildStyle, bs.p0]}
			/>
		);
	};

	const onRenderMeetingConfig = (props: RouteComponentProps<any>) => {
		const locationState: IMeetingConfigModalLocationState = fullscreenModal.history.location
			? fullscreenModal.history.location.state || {}
			: {};
		return (
			<MeetingConfigModal
				{...props}
				meeting={locationState.viewModel}
				onClose={close}
				styles={[styleSheet.defaultChildStyle]}
			/>
		);
	};

	const onRenderStaticDrawerDemo = (props: RouteComponentProps<any>) => {
		return <DemoContent {...props} />;
	};

	const close = () => {
		fullscreenModal.history.goBackToLocationBeforeTrackedHistory(true);
	};

	return (
		<FullscreenModal loggingCategory='WebFullscreenModal'>
			<Redirect from='/people/:id' to={`${ROUTES.contacts.people}/:id`} />
			<PrivateRoute path={`${ROUTES.contacts.people}/:id`} render={onRenderContact} userSession={userSession} />
			<Redirect from='/companies/:id' to={`${ROUTES.contacts.companies}/:id`} />
			<PrivateRoute path={`${ROUTES.contacts.companies}/:id`} render={onRenderCompany} userSession={userSession} />
			<PrivateRoute path='/dataBoards/opportunities/:boardId/:id' userSession={userSession}>
				<Opportunity styles={[styleSheet.defaultChildStyle]} />
			</PrivateRoute>
			<PrivateRoute path='/email/bulk-send-cancelled' render={onRenderBulkEmailCancelled} userSession={userSession} />
			<PrivateRoute
				path='/automationsonhold/:resourceSelectorId'
				render={onRenderAutomationsOnHold}
				userSession={userSession}
			/>
			<PrivateRoute
				path='/email/campaigns/edit'
				exact={false}
				render={onRenderEditQueuedCampaign}
				userSession={userSession}
			/>
			<PrivateRoute path='/email/campaigns/create/campaign-follow-up/:campaignId' userSession={userSession}>
				<CampaignFollowupPage />
			</PrivateRoute>
			<PrivateRoute path='/email/campaigns/create/from-template/:templateId' userSession={userSession}>
				<CampaignFromTemplatePage />
			</PrivateRoute>
			<PrivateRoute onLoad={requireAICampaigns} path='/email/campaigns/create/ai' userSession={userSession}>
				<CreateAICampaign />
			</PrivateRoute>
			<PrivateRoute onLoad={requireAICampaigns} path='/email/campaigns/:campaignId/ai-review' userSession={userSession}>
				<AICampaignReviewPage />
			</PrivateRoute>
			<PrivateRoute path='/email/campaigns/create' render={onRenderBrowseAndSend} userSession={userSession} />
			<PrivateRoute path='/email/campaigns/review' userSession={userSession}>
				<ReviewEmailCampaign />
			</PrivateRoute>
			<PrivateRoute path='/integrations/ownership' render={onRenderIntegrationOwnership} userSession={userSession} />
			<PrivateRoute path='/meeting/config' render={onRenderMeetingConfig} userSession={userSession} />
			{/*
			 * Its own route AND its own key. A separate route alone would still reconcile in place —
			 * Switch would swap one PrivateRoute for another of the same type — and the composer reads
			 * its seed once at mount, so the revision context would be dropped.
			 */}
			<PrivateRoute path='/social-media/post/create/from-reged-revision' userSession={userSession}>
				<EditSocialMediaPost key='reged-revision' />
			</PrivateRoute>
			<PrivateRoute
				path={[
					'/social-media/post/create/from-template',
					'/social-media/post/create',
					'/social-media/post/edit-post-draft/:id',
					'/social-media/post/edit-template/:templateId/:suggestionId',
					'/social-media/post/create-from-review',
				]}
				userSession={userSession}
			>
				<EditSocialMediaPost />
			</PrivateRoute>
			<PrivateRoute path='/social-media/post/review/from-template' userSession={userSession}>
				<ReviewSocialPost />
			</PrivateRoute>
			<PrivateRoute path='/social-media/post/edit-from-post-report' userSession={userSession}>
				<EditSocialMediaPost
					onPostUpdated={() => {
						if (fullscreenModal.history.location.state?.referrer) {
							fullscreenModal.history.goBack();
						} else {
							close();
						}
						return true;
					}}
				/>
			</PrivateRoute>
			<PrivateRoute path='/social-media/post/approval-preview' userSession={userSession}>
				<ApprovalPreviewSocialMediaPost routeContainerClassName={css(styleSheet.defaultChildStyle)} />
			</PrivateRoute>
			<PrivateRoute
				path={[
					'/social-media/post/create/success',
					'/social-media/post/edit-template/:templateId/:suggestionId/success',
				]}
				userSession={userSession}
			>
				{() => {
					const locationState =
						fullscreenModal?.history?.location?.state || ({} as ILocationState<SocialMediaPostViewModel, null>);
					const isContentGenerationEnabled = userSession?.account?.features?.contentGeneration?.enabled;
					const requiresCompliance = accountHasComplianceRequired(userSession);

					return isContentGenerationEnabled ? (
						<AIScheduleSuccess
							resourceType='social'
							socialPost={locationState.socialPost}
							requiresCompliance={requiresCompliance || locationState.socialPost?.sendWithCompliance}
							multiple={locationState.multiple}
						/>
					) : (
						<SocialMediaPostScheduleComplete
							post={locationState.socialPost}
							onClose={() => fullscreenModal.dismissModal()}
						/>
					);
				}}
			</PrivateRoute>
			<PrivateRoute path='/postcard/:templateId/:step' userSession={userSession}>
				<PostcardComposer />
			</PrivateRoute>
			<PrivateRoute path='/bulk-birthday-postcard/:templateId' userSession={userSession}>
				<BulkHwcBirthdayComposerPage />
			</PrivateRoute>
			<PrivateRoute path='/bulk-contact-selection-postcard/:templateId' userSession={userSession}>
				<BulkHwcFromContactSelectionPage />
			</PrivateRoute>
			<PrivateRoute path='/bulk-contact-filter-postcard/:templateId' userSession={userSession}>
				<BulkHwcFromContactFilterPage />
			</PrivateRoute>
			<PrivateRoute path='/texting-campaign' userSession={userSession}>
				<TextingCampaignRoutes />
			</PrivateRoute>
			<PrivateRoute
				path='/reporting/advanced-reporting/:automationId'
				render={onRenderAdvancedReportingDashboard}
				userSession={userSession}
			/>
			<PrivateRoute
				path='/reporting/advanced-opportunity-reporting/:boardId'
				userSession={userSession}
				render={props => (
					<Redirect
						to={`${ROUTES.dataBoards.opportunitiesAdvancedReporting}?boardId=${encodeURIComponent(props.match.params.boardId)}`}
					/>
				)}
			/>
			<PrivateRoute
				path='/reporting/advanced-donations-reporting'
				render={onRenderAdvancedReportingDashboard}
				userSession={userSession}
			>
				<AdvancedDonationReporting />
			</PrivateRoute>
			<PrivateRoute path='/blog' userSession={userSession}>
				<BlogCampaignRoutes />
			</PrivateRoute>
			<PrivateRoute path='/demo/static-drawer' render={onRenderStaticDrawerDemo} userSession={userSession} />
			<PrivateRoute path='/multi-campaign-scheduler/email' userSession={userSession}>
				<MultiEmailScheduler />
			</PrivateRoute>
			<PrivateRoute path='/multi-campaign-scheduler/transition' userSession={userSession}>
				<SequenceTransitionScreen />
			</PrivateRoute>
			<PrivateRoute path='/multi-campaign-scheduler' userSession={userSession}>
				<MultiCampaignSchedulerRoutes />
			</PrivateRoute>
		</FullscreenModal>
	);
};

// Both flags, matching the server: the feature is inert without content generation, so an account with only the
// campaign flag would reach a screen whose every request is refused.
const requireAICampaigns = (_routerLocation: IRouterLocation, userSession: UserSessionContext) =>
	userSession?.account?.features?.aiEmailCampaign?.enabled && userSession?.account?.features?.contentGeneration?.enabled
		? null
		: '/dashboard';

export const WebFullscreenModal = observer(_WebFullscreenModal);
